import { useCallback, useState } from 'react';
import { isAxiosError } from 'axios';
import { useSnackbar } from 'app/hooks/useSnackbar';
import { cacheService } from 'app/utils/cacheService';
import {
  DEFAULT_PAYMENT_PAGE_SIZE,
  getEmptyPaymentsResponse,
  TMultiSplitPaymentsResponse,
  TPayoutReceipt,
  TUpdatePaymentAccountPayload,
} from 'modules/products/interfaces/multiSplitPayments';
import { MultiSplitPaymentsHttpService } from '../api/multiSplitPaymentsHttpService';

const PAYMENTS_CACHE_PREFIX = 'multisplit-payments';
const PAYMENTS_CACHE_TTL = 3 * 60 * 1000;

const getPaymentsCacheKey = (
  applicationId: string,
  page: number,
  size: number,
) => `${PAYMENTS_CACHE_PREFIX}:${applicationId}:${page}:${size}`;

const invalidatePaymentsCache = (applicationId: string) => {
  cacheService
    .getStats()
    .keys.filter((key) =>
      key.startsWith(`${PAYMENTS_CACHE_PREFIX}:${applicationId}:`),
    )
    .forEach((key) => cacheService.remove(key));
};

const normalizePaymentsResponse = (
  data: TMultiSplitPaymentsResponse | null | undefined,
  page: number,
  size: number,
): TMultiSplitPaymentsResponse => {
  if (!data || !Array.isArray(data.content)) {
    return getEmptyPaymentsResponse(page, size);
  }

  return {
    ...data,
    content: data.content,
    total_elements: data.total_elements ?? 0,
    total_pages: data.total_pages ?? 0,
    page: data.page ?? page,
    size: data.size ?? size,
    has_next: Boolean(data.has_next),
  };
};

const useMultiSplitPaymentsService = () => {
  const { showSnackbar } = useSnackbar();
  const [isGetApplicationPaymentsLoading, setIsGetApplicationPaymentsLoading] =
    useState(false);
  const [isGetApplicationReceiptsLoading, setIsGetApplicationReceiptsLoading] =
    useState(false);
  const [isDownloadReceiptLoading, setIsDownloadReceiptLoading] =
    useState(false);
  const [isReprocessPaymentLoading, setIsReprocessPaymentLoading] =
    useState(false);
  const [isUpdatePaymentAccountLoading, setIsUpdatePaymentAccountLoading] =
    useState(false);

  const getApplicationPayments = useCallback(
    async (
      applicationId: string,
      page = 0,
      size = DEFAULT_PAYMENT_PAGE_SIZE,
    ): Promise<TMultiSplitPaymentsResponse> => {
      const cacheKey = getPaymentsCacheKey(applicationId, page, size);
      const cached = cacheService.get<TMultiSplitPaymentsResponse>(cacheKey);
      if (cached) {
        return normalizePaymentsResponse(cached, page, size);
      }

      try {
        setIsGetApplicationPaymentsLoading(true);
        const multiSplitPaymentsService = new MultiSplitPaymentsHttpService();
        multiSplitPaymentsService.setHeader({
          handled: true,
        });
        const response = await multiSplitPaymentsService.getApplicationPayments(
          applicationId,
          page,
          size,
        );
        const normalized = normalizePaymentsResponse(response, page, size);

        cacheService.set(cacheKey, normalized, PAYMENTS_CACHE_TTL);

        return normalized;
      } catch (error) {
        console.error('Erro getApplicationPayments:', error);
        showSnackbar('Ocorreu um erro ao buscar os pagamentos');
        return getEmptyPaymentsResponse(page, size);
      } finally {
        setIsGetApplicationPaymentsLoading(false);
      }
    },
    [],
  );

  const getApplicationReceipts = useCallback(
    async (applicationId: string): Promise<TPayoutReceipt[]> => {
      try {
        setIsGetApplicationReceiptsLoading(true);
        const multiSplitPaymentsService = new MultiSplitPaymentsHttpService();
        multiSplitPaymentsService.setHeader({
          handled: true,
        });
        const res = await multiSplitPaymentsService.getApplicationReceipts(
          applicationId,
        );

        return (res?.receipts || []).filter((receipt) => receipt.payout_id);
      } catch (error) {
        console.error('Erro getApplicationReceipts:', error);
        return [];
      } finally {
        setIsGetApplicationReceiptsLoading(false);
      }
    },
    [],
  );

  const getReceiptPdf = useCallback(
    async (payoutId: string): Promise<ArrayBuffer | null> => {
      try {
        setIsDownloadReceiptLoading(true);
        const multiSplitPaymentsService = new MultiSplitPaymentsHttpService();
        multiSplitPaymentsService.setRequestConfig({
          responseType: 'arraybuffer',
          headers: {
            handled: true,
          },
        });
        const result = await multiSplitPaymentsService.getReceiptPdf(payoutId);
        return result;
      } catch (error) {
        console.error('Erro getReceiptPdf:', error);
        showSnackbar(
          'Não foi possível baixar o comprovante de desembolso. Tente novamente.',
        );
        return null;
      } finally {
        setIsDownloadReceiptLoading(false);
      }
    },
    [showSnackbar],
  );

  const updatePaymentAccount = useCallback(
    async (
      applicationId: string,
      accountId: string,
      payload: TUpdatePaymentAccountPayload,
    ): Promise<{ ok: true } | { ok: false; status?: number }> => {
      try {
        setIsUpdatePaymentAccountLoading(true);
        const multiSplitPaymentsService = new MultiSplitPaymentsHttpService();
        multiSplitPaymentsService.setHeader({
          handled: true,
        });
        await multiSplitPaymentsService.updatePaymentAccount(
          accountId,
          payload,
        );
        invalidatePaymentsCache(applicationId);
        return { ok: true };
      } catch (error) {
        console.error('Erro updatePaymentAccount:', error);
        return {
          ok: false,
          status: isAxiosError(error) ? error.response?.status : undefined,
        };
      } finally {
        setIsUpdatePaymentAccountLoading(false);
      }
    },
    [],
  );

  const reprocessApplicationPayment = useCallback(
    async (applicationId: string, paymentId: string): Promise<boolean> => {
      try {
        setIsReprocessPaymentLoading(true);
        const multiSplitPaymentsService = new MultiSplitPaymentsHttpService();
        multiSplitPaymentsService.setHeader({
          handled: true,
        });
        await multiSplitPaymentsService.reprocessApplicationPayment(
          applicationId,
          paymentId,
        );
        invalidatePaymentsCache(applicationId);
        return true;
      } catch (error) {
        console.error('Erro reprocessApplicationPayment:', error);
        return false;
      } finally {
        setIsReprocessPaymentLoading(false);
      }
    },
    [],
  );

  return {
    isGetApplicationPaymentsLoading,
    isGetApplicationReceiptsLoading,
    isDownloadReceiptLoading,
    isReprocessPaymentLoading,
    isUpdatePaymentAccountLoading,
    getApplicationPayments,
    getApplicationReceipts,
    getReceiptPdf,
    reprocessApplicationPayment,
    updatePaymentAccount,
  };
};

export default useMultiSplitPaymentsService;
